import {
  createFeatureFlagService,
  type FeatureFlagService,
} from "../../services/feature-flags";
import {
  CALIBRATION_PASSAGE,
  calculateCalibration,
  createBrowserSpeechOutService,
  loadVoiceOptions,
  preferredOfflineVoice,
  targetWordsPerMinute,
  type DeviceVoice,
  type SpeechSynthesisAdapter,
  type SpeechUtterance,
} from "../../services/speech";
import { createVersionedStorageService } from "../../services/versioned-storage";
import {
  decideAutomaticWorkflow,
  describeAutomaticWorkflow,
  REGISTRATION_UNAVAILABLE,
  type AutomaticWorkflowDecision,
} from "./automatic-orders";
import { mountOrdersDelivery, type DeliverySpeechSetup } from "./delivery-ui";
import {
  mountTraceGridRegistration,
  recognizeTraceGridCandidates,
  type GridFillTarget,
} from "./grid-recognition";
import {
  createPreflightSession,
  missingRequiredFills,
  preflightStorageKey,
  savePreflightValues,
  type PreflightSession,
} from "./preflight";
import { resolveOrderSetSelection } from "./order-set";
import type { OrderSetFileInput } from "./order-set/types";
import { mountConfirmatoryQuestions } from "./questions-ui";
import { mountTracePicker, type TraceAssetSelection } from "./trace";

/**
 * Decides whether the trainee is shown an automatic result or the manual
 * recovery path. No local trace-to-basemap matcher exists yet, so registration
 * is reported as unavailable in every build and the manual controls stay
 * visible. Handover step 6 supplies the matcher behind the `automaticOrders`
 * flag; nothing else here has to change when it does.
 */
function workflowDecision(
  session: PreflightSession,
  values: Readonly<Record<string, string>>,
): AutomaticWorkflowDecision {
  return decideAutomaticWorkflow(
    REGISTRATION_UNAVAILABLE,
    session.blanks.map((blank) => ({
      fieldType: blank.fieldType,
      id: blank.id,
      required: blank.required,
      resolved: (values[blank.id] ?? blank.value).trim() !== "",
    })),
  );
}

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function band(title: string): HTMLElement {
  const section = element("section");
  section.className = "preflight-band";
  section.append(element("h3", title));
  return section;
}

function valuesFromForm(
  form: HTMLFormElement,
): Readonly<Record<string, string>> {
  return Object.fromEntries(
    Array.from(form.elements)
      .filter(
        (control): control is HTMLInputElement =>
          control instanceof HTMLInputElement,
      )
      .filter(({ name }) => name !== "")
      .map(({ name, value }) => [name, value]),
  );
}

function mountCalibration(
  target: HTMLElement,
  storage: ReturnType<typeof createVersionedStorageService>,
  flags: FeatureFlagService,
  onSetup: (setup: DeliverySpeechSetup | null) => void,
): () => void {
  if (!flags.isEnabled("speechOutput")) {
    target.append(element("p", "Speech output is disabled in this build."));
    onSetup(null);
    return () => undefined;
  }
  if (
    !("speechSynthesis" in window) ||
    typeof SpeechSynthesisUtterance === "undefined"
  ) {
    target.append(
      element(
        "p",
        "Speech synthesis is unavailable. On Brave, confirm that Shields is not blocking the browser speech service.",
      ),
    );
    onSetup(null);
    return () => undefined;
  }

  const nativeSynthesis = window.speechSynthesis;
  const synthesis: SpeechSynthesisAdapter = {
    cancel: () => nativeSynthesis.cancel(),
    getVoices: () => nativeSynthesis.getVoices(),
    pause: () => nativeSynthesis.pause(),
    resume: () => nativeSynthesis.resume(),
    speak: (utterance) =>
      nativeSynthesis.speak(utterance as unknown as SpeechSynthesisUtterance),
  };
  const speech = createBrowserSpeechOutService(
    synthesis,
    (text) => new SpeechSynthesisUtterance(text) as unknown as SpeechUtterance,
    {
      clearInterval: (handle) => window.clearInterval(handle),
      clearTimeout: (handle) => window.clearTimeout(handle),
      setInterval: (callback, delayMs) => window.setInterval(callback, delayMs),
      setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
    },
    {
      get hidden() {
        return document.hidden;
      },
      addEventListener: (_, listener) =>
        document.addEventListener("visibilitychange", listener),
      removeEventListener: (_, listener) =>
        document.removeEventListener("visibilitychange", listener),
    },
  );

  const voiceSelect = element("select");
  voiceSelect.dataset.role = "speech-voice";
  voiceSelect.disabled = true;
  const modeSelect = element("select");
  modeSelect.dataset.role = "speech-rate-mode";
  for (const [value, label] of [
    ["copy", "Copy - 90 wpm"],
    ["standard", "Standard - 100 wpm"],
    ["pressure", "Pressure - 130 to 180 wpm"],
    ["free", "Free - 80 to 180 wpm"],
  ] as const) {
    const option = element("option", label);
    option.value = value;
    modeSelect.append(option);
  }
  modeSelect.value = "standard";
  const rate = element("input");
  rate.type = "range";
  rate.min = "80";
  rate.max = "180";
  rate.value = "100";
  rate.disabled = true;
  rate.dataset.role = "speech-rate";
  const rateValue = element("output", "100 wpm");
  const calibrate = element("button", "Calibrate selected local voice");
  calibrate.type = "button";
  calibrate.disabled = true;
  calibrate.dataset.role = "speech-calibrate";
  const status = element("p", "Loading voices reported by this device...");
  status.dataset.role = "speech-calibration-status";

  const updateRate = (): void => {
    const mode = modeSelect.value;
    if (mode === "copy" || mode === "standard") {
      const fixed = targetWordsPerMinute(mode);
      rate.value = String(fixed);
      rate.disabled = true;
    } else {
      rate.disabled = false;
      rate.min = mode === "pressure" ? "130" : "80";
      if (Number(rate.value) < Number(rate.min)) rate.value = rate.min;
    }
    rateValue.value = `${rate.value} wpm`;
    rateValue.textContent = rateValue.value;
  };
  const showSavedCalibration = (): void => {
    const selected = voiceSelect.value;
    const loaded = storage.load(preflightStorageKey("calibration", selected));
    const data = loaded.ok ? loaded.value.data : null;
    if (
      data === null ||
      !("voiceId" in data) ||
      !("wordsPerMinute" in data) ||
      data.voiceId !== selected
    ) {
      status.textContent = "This local voice requires calibration.";
      onSetup(null);
      return;
    }
    const targetWpm = targetWordsPerMinute(
      modeSelect.value as "copy" | "free" | "pressure" | "standard",
      Number(rate.value),
    );
    const calibration = calculateCalibration(
      3_600_000 / data.wordsPerMinute,
      targetWpm,
    );
    status.textContent = calibration.reachable
      ? `Calibrated at ${data.wordsPerMinute.toFixed(1)} wpm; ${String(targetWpm)} wpm uses a ${calibration.clampedMultiplier.toFixed(2)} multiplier.`
      : `Finding: this voice cannot reach ${String(targetWpm)} wpm within the 0.5 to 2.0 multiplier clamp.`;
    onSetup(
      calibration.reachable
        ? {
            baseMultiplier: calibration.clampedMultiplier,
            speech,
            targetWpm,
            voiceId: selected,
            voiceLabel:
              voiceSelect.selectedOptions.item(0)?.textContent ?? selected,
          }
        : null,
    );
  };
  modeSelect.addEventListener("change", () => {
    updateRate();
    showSavedCalibration();
  });
  rate.addEventListener("input", () => {
    updateRate();
    showSavedCalibration();
  });
  voiceSelect.addEventListener("change", showSavedCalibration);
  calibrate.addEventListener("click", () => {
    const voiceId = voiceSelect.value;
    const selected = nativeSynthesis
      .getVoices()
      .find((voice) => voice.voiceURI === voiceId && voice.localService);
    if (selected === undefined) {
      status.textContent = "Choose a local voice before calibration.";
      return;
    }
    const mode = modeSelect.value as "copy" | "free" | "pressure" | "standard";
    const targetWpm = targetWordsPerMinute(mode, Number(rate.value));
    let startedAt: number | undefined;
    calibrate.disabled = true;
    onSetup(null);
    status.textContent = "Calibration passage is playing.";
    const operation = speech.speak({
      text: CALIBRATION_PASSAGE,
      voiceId,
      rate: 1,
      onStart: () => {
        startedAt = performance.now();
      },
    });
    if (!(operation instanceof Promise)) {
      status.textContent = "Speech output is unavailable in this build.";
      calibrate.disabled = false;
      return;
    }
    void operation
      .then(() => {
        if (startedAt === undefined) {
          throw new Error("The device did not report speech start time.");
        }
        const result = calculateCalibration(
          performance.now() - startedAt,
          targetWpm,
        );
        const saved = storage.save(
          "calibration",
          preflightStorageKey("calibration", voiceId),
          { voiceId, wordsPerMinute: result.baselineWpm },
        );
        if (!saved.ok) throw new Error(saved.error.message);
        showSavedCalibration();
      })
      .catch((error: unknown) => {
        status.textContent =
          error instanceof Error ? error.message : "Calibration failed.";
      })
      .finally(() => {
        calibrate.disabled = false;
      });
  });

  target.append(
    element("label", "Voice "),
    voiceSelect,
    element("label", " Rate mode "),
    modeSelect,
    rate,
    rateValue,
    calibrate,
    status,
  );
  const voiceSource = {
    addEventListener: (_: "voiceschanged", listener: () => void) =>
      nativeSynthesis.addEventListener("voiceschanged", listener),
    getVoices: (): readonly DeviceVoice[] => nativeSynthesis.getVoices(),
    removeEventListener: (_: "voiceschanged", listener: () => void) =>
      nativeSynthesis.removeEventListener("voiceschanged", listener),
  };
  void loadVoiceOptions(voiceSource, {
    clearTimeout: (handle) => window.clearTimeout(handle),
    setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
  }).then((voices) => {
    voiceSelect.replaceChildren();
    for (const voice of voices) {
      const option = element("option", voice.label);
      option.value = voice.id;
      option.disabled = !voice.isLocal;
      voiceSelect.append(option);
    }
    const preferred = preferredOfflineVoice(voices);
    if (preferred === null) {
      status.textContent =
        "Finding: this device reports no local speech voice. Network voices cannot be used offline.";
      onSetup(null);
      return;
    }
    voiceSelect.value = preferred.id;
    voiceSelect.disabled = false;
    calibrate.disabled = false;
    showSavedCalibration();
  });
  return () => {
    speech.cancel();
    speech.dispose();
  };
}

function renderSession(
  target: HTMLElement,
  session: PreflightSession,
  storage: ReturnType<typeof createVersionedStorageService>,
  flags: FeatureFlagService,
  traceSelection: () => TraceAssetSelection,
): () => void {
  target.replaceChildren();

  const fileBand = band("File");
  fileBand.append(
    element(
      "p",
      `${String(session.parseSummary.paragraphCount)} paragraphs parsed. ${String(session.parseSummary.warningCount)} parser warnings.`,
    ),
  );
  if (session.issues.length > 0 || session.parserWarnings.length > 0) {
    const issues = element("ul");
    for (const issue of session.issues)
      issues.append(element("li", issue.message));
    for (const warning of session.parserWarnings)
      issues.append(element("li", warning.message));
    fileBand.append(issues);
  }

  const blanksBand = band("Blanks");
  const form = element("form");
  const requiredStatus = element("p");
  requiredStatus.dataset.role = "required-fill-status";
  let refreshStart = (): void => undefined;
  const gridInputs: HTMLInputElement[] = [];
  const fillTargets: GridFillTarget[] = [];
  for (const blank of session.blanks) {
    const label = element("label", `${blank.prompt} (${blank.fieldType}) `);
    const input = element("input");
    input.name = blank.id;
    input.value = blank.value;
    input.autocomplete = "off";
    input.required = blank.required;
    input.dataset.blankId = blank.id;
    if (blank.fieldType === "grid") gridInputs.push(input);
    if (blank.required) {
      fillTargets.push({
        input,
        isKnownGrid: blank.fieldType === "grid",
        prompt: blank.prompt,
      });
    }
    label.append(input);
    form.append(label);
  }
  if (session.blanks.length === 0)
    form.append(element("p", "No blanks detected."));
  form.addEventListener("input", () => {
    const issue = savePreflightValues(
      storage,
      "fills",
      session.fileHash,
      valuesFromForm(form),
    );
    if (issue !== null) form.dataset.storageError = issue.message;
    else delete form.dataset.storageError;
    refreshStart();
  });
  const gridAssist = element("div");
  gridAssist.className = "grid-assist";
  const workflowStatus = element("p");
  workflowStatus.dataset.role = "grid-workflow-status";
  gridAssist.append(workflowStatus);
  const renderWorkflowStatus = (
    values: Readonly<Record<string, string>>,
  ): AutomaticWorkflowDecision => {
    const decision = workflowDecision(session, values);
    workflowStatus.textContent = describeAutomaticWorkflow(decision);
    workflowStatus.dataset.mode = decision.mode;
    return decision;
  };
  const initialDecision = renderWorkflowStatus({});
  const gridCandidates = element("datalist");
  gridCandidates.id = `grid-candidates-${session.fileHash.slice(0, 12)}`;
  for (const input of gridInputs) input.setAttribute("list", gridCandidates.id);
  const shouldOfferGridScan =
    gridInputs.length > 0 || session.documentKind === "warning-order";
  if (shouldOfferGridScan) {
    const scan = element("button", "Scan trace for grid references");
    scan.type = "button";
    scan.dataset.role = "grid-scan";
    const scanStatus = element(
      "p",
      "OCR candidates are local suggestions. Confirm every grid against the trace before using it.",
    );
    scanStatus.dataset.role = "grid-scan-status";
    scan.disabled = !flags.isEnabled("gridRecognition");
    scan.addEventListener("click", () => {
      const image = traceSelection().imageFile;
      if (image === null) {
        scanStatus.textContent = "Select the trace image before scanning.";
        scanStatus.dataset.status = "error";
        return;
      }
      scan.disabled = true;
      scanStatus.textContent = "Loading the local OCR engine...";
      delete scanStatus.dataset.status;
      void recognizeTraceGridCandidates(image, ({ progress, status }) => {
        scanStatus.textContent = `${status}: ${String(Math.round(progress * 100))}%`;
      })
        .then(({ candidates, confidence }) => {
          gridCandidates.replaceChildren();
          const candidateList = element("ul");
          candidateList.dataset.role = "grid-candidates";
          for (const candidate of candidates) {
            const option = element("option");
            option.value = candidate.value;
            option.label = candidate.label;
            gridCandidates.append(option);
            candidateList.append(element("li", candidate.label));
          }
          const confidenceText = `${String(Math.round(confidence))}% page confidence`;
          scanStatus.textContent =
            candidates.length === 0
              ? `No explicit grid references were found (${confidenceText}). Enter them manually from the trace.`
              : `${String(candidates.length)} possible grid reference${candidates.length === 1 ? "" : "s"} found (${confidenceText}). Confirm visually, then choose or type the value in each grid field.`;
          scanStatus.dataset.status =
            candidates.length === 0 ? "error" : "ready";
          gridAssist.querySelector("ul")?.remove();
          if (candidates.length > 0) gridAssist.append(candidateList);
        })
        .catch(() => {
          scanStatus.textContent =
            "The local grid scan failed. Enter the grids manually from the trace.";
          scanStatus.dataset.status = "error";
        })
        .finally(() => {
          scan.disabled = !flags.isEnabled("gridRecognition");
        });
    });
    gridAssist.append(scan, scanStatus, gridCandidates);
    if (initialDecision.manualGridRegistrationOffered) {
      mountTraceGridRegistration(gridAssist, traceSelection, fillTargets);
    }
  }
  blanksBand.append(requiredStatus, form, gridAssist);

  const lexiconBand = band("Unresolved tokens");
  if (session.unresolvedTokens.length === 0) {
    lexiconBand.append(element("p", "None."));
  } else {
    const list = element("ul");
    for (const token of session.unresolvedTokens) {
      const item = element("li", token);
      item.className = "unresolved-token";
      list.append(item);
    }
    lexiconBand.append(
      list,
      element("p", "These entries do not block playback."),
    );
  }

  // R1 Step 14. Its own band, behind its own flag, after delivery. It alters
  // no part of the delivery path.
  const questionsBand = band("Confirmatory questions");
  mountConfirmatoryQuestions(questionsBand, {
    fileHash: session.fileHash,
    flags,
    storage,
    tree: session.ordersTree,
  });

  const precisionBand = band("Grid precision");
  if (session.gridPrecisionWarnings.length === 0) {
    precisionBand.append(
      element("p", "Every grid in this document is six-figure."),
    );
  } else {
    const list = element("ul");
    list.dataset.role = "grid-precision-warnings";
    for (const warning of session.gridPrecisionWarnings) {
      const entry = element("li", warning.message);
      entry.dataset.status = "error";
      list.append(entry);
    }
    precisionBand.append(
      list,
      element(
        "p",
        "These do not block delivery and are still spoken digit by digit.",
      ),
    );
  }

  const cuesBand = band("Cues");
  const cueList = element("ul");
  for (const cue of session.cues) {
    cueList.append(
      element(
        "li",
        `${cue.name}: ${cue.nodeId ?? "not available"} (${cue.source})`,
      ),
    );
  }
  cuesBand.append(cueList);

  const calibrationBand = band("Calibration");
  const start = element("button", "Start delivery");
  start.type = "button";
  start.disabled = true;
  start.dataset.preflightReady = String(session.canStartPlayback);
  start.dataset.role = "delivery-start";
  // A disabled control states why it is disabled, in visible text beside
  // itself. A tooltip is not visible on a touch device and a dead control
  // with no explanation is its own defect.
  const startReason = element("p");
  startReason.className = "start-reason";
  startReason.dataset.role = "delivery-start-reason";
  let deliveryCleanup = (): void => undefined;
  let speechSetup: DeliverySpeechSetup | null = null;
  refreshStart = (): void => {
    const missing = missingRequiredFills(session.blanks, valuesFromForm(form));
    const deliveryEnabled = flags.isEnabled("delivery");
    start.disabled =
      !deliveryEnabled || speechSetup === null || missing.length > 0;
    start.dataset.preflightReady = String(missing.length === 0);
    // Never claim readiness while the start control is dead. Fields being
    // complete is not the same as delivery being able to start.
    requiredStatus.textContent =
      missing.length > 0
        ? `${String(missing.length)} required field${missing.length === 1 ? " is" : "s are"} incomplete. ATLas will not deliver an incomplete warning order or an unresolved grid.`
        : start.disabled
          ? "Every required field is complete, but delivery cannot start yet."
          : "Every required field is complete.";
    requiredStatus.dataset.status =
      missing.length === 0 && !start.disabled ? "ready" : "error";
    renderWorkflowStatus(valuesFromForm(form));
    const reason = !deliveryEnabled
      ? "Orders delivery is disabled in this build."
      : missing.length > 0
        ? `Delivery cannot start: ${String(missing.length)} required field${missing.length === 1 ? " is" : "s are"} still incomplete.`
        : speechSetup === null
          ? "Delivery cannot start: calibrate a local voice in the Calibration section above."
          : null;
    startReason.textContent = reason ?? "";
    startReason.hidden = reason === null;
    startReason.dataset.status = reason === null ? "ready" : "error";
    start.title = reason ?? "Start delivery using the selected local voice.";
  };
  const calibrationCleanup = mountCalibration(
    calibrationBand,
    storage,
    flags,
    (setup) => {
      speechSetup = setup;
      refreshStart();
    },
  );
  refreshStart();
  start.addEventListener("click", () => {
    if (speechSetup === null || start.disabled) return;
    deliveryCleanup();
    deliveryCleanup = mountOrdersDelivery(target, {
      fills: valuesFromForm(form),
      flags,
      session,
      speechSetup,
      storage,
      traceSelection: traceSelection(),
    });
  });
  target.append(
    fileBand,
    blanksBand,
    lexiconBand,
    precisionBand,
    cuesBand,
    questionsBand,
    calibrationBand,
    start,
    startReason,
  );
  return () => {
    deliveryCleanup();
    calibrationCleanup();
  };
}

export function mountOrdersReaderPreflight(container: HTMLElement): void {
  const controls = element("div");
  controls.className = "preflight";
  const label = element("label", "Orders or warning-order Markdown file ");
  const input = element("input");
  input.type = "file";
  input.accept = "text/markdown,text/plain,.md";
  input.dataset.role = "orders-file";
  label.append(input);
  const output = element(
    "div",
    "Choose a local Markdown file to begin pre-flight.",
  );
  output.dataset.role = "preflight-output";
  const flags = createFeatureFlagService();
  const traceBand = band("Trace");
  const tracePicker = mountTracePicker(traceBand, flags);

  // Change 2: one selection loads a whole serial. Loading is one action;
  // delivery is not, and nothing here starts one. Ruling 17 stands, so the
  // trainee still chooses which document to take and starts it himself.
  const setBand = band("Order set");
  const setLabel = element(
    "label",
    "Warning order, orders and trace, selected together ",
  );
  const setInput = element("input");
  setInput.type = "file";
  setInput.multiple = true;
  setInput.dataset.role = "order-set-files";
  setLabel.append(setInput);
  const setStatus = element(
    "p",
    "Select all three files of one serial in a single action.",
  );
  setStatus.dataset.role = "order-set-status";
  const setChoices = element("div");
  setChoices.className = "order-set-choices";
  setBand.append(setLabel, setStatus, setChoices);

  controls.append(setBand, label, traceBand, output);
  container.append(controls);

  const storage = createVersionedStorageService(window.localStorage, () =>
    new Date().toISOString(),
  );
  let activeCleanup = (): void => undefined;

  const loadDocument = (file: File): void => {
    activeCleanup();
    activeCleanup = (): void => undefined;
    output.textContent = "Parsing local file...";
    void file
      .text()
      .then(
        async (source) => await createPreflightSession(source, storage, flags),
      )
      .then((result) => {
        if (!result.ok) {
          output.textContent = result.error.message;
          output.dataset.status = "error";
          return;
        }
        output.dataset.status = "ready";
        activeCleanup = renderSession(
          output,
          result.session,
          storage,
          flags,
          () => tracePicker.selection(),
        );
      })
      .catch((error: unknown) => {
        output.textContent =
          error instanceof Error
            ? error.message
            : "The local file could not be read.";
        output.dataset.status = "error";
      });
  };

  /**
   * The language layer is read from the document content, per doc 04 section
   * 5.5, so the text has to be in hand before the selection can be resolved.
   * Only formats that carry text are read; an image contributes `null`.
   */
  const readSelection = async (
    chosen: readonly File[],
  ): Promise<readonly OrderSetFileInput[]> =>
    await Promise.all(
      chosen.map(async (file) => ({
        name: file.name,
        text: /\.(?:md|markdown)$/i.test(file.name)
          ? await file.text().catch(() => null)
          : null,
      })),
    );

  const resolveSelection = async (): Promise<void> => {
    setChoices.replaceChildren();
    const chosen = Array.from(setInput.files ?? []);
    const resolved = resolveOrderSetSelection(await readSelection(chosen));
    if (!resolved.ok) {
      setStatus.textContent = resolved.message;
      setStatus.dataset.status = "error";
      return;
    }
    const byName = new Map(chosen.map((file) => [file.name, file]));
    const { languageLayer, ordersCard, setId, traceImage, warningOrder } =
      resolved.set;
    setStatus.textContent = `Set ${setId} loaded, ${languageLayer === "unmarked" ? "single language" : languageLayer.toUpperCase()}. Nothing is playing. The trace is held and is not shown here; it is issued during the run.`;
    setStatus.dataset.status = "ready";
    void traceImage;

    for (const [role, file] of [
      ["Take the warning order", warningOrder],
      ["Take the orders", ordersCard],
    ] as const) {
      const chooser = element("button", role);
      chooser.type = "button";
      chooser.dataset.role =
        role === "Take the orders" ? "take-orders" : "take-warning-order";
      const source = byName.get(file.name);
      chooser.disabled = source === undefined;
      chooser.addEventListener("click", () => {
        if (source === undefined) return;
        loadDocument(source);
      });
      setChoices.append(chooser);
    }
  };

  setInput.addEventListener("change", () => {
    setStatus.textContent = "Reading the selection...";
    setStatus.dataset.status = "working";
    void resolveSelection().catch((error: unknown) => {
      // Doc 02 section 4: a failure surfaces, never silently.
      setStatus.textContent =
        error instanceof Error
          ? error.message
          : "That selection could not be read.";
      setStatus.dataset.status = "error";
    });
  });

  input.addEventListener("change", () => {
    const file = input.files?.item(0);
    if (file === null || file === undefined) return;
    loadDocument(file);
  });
}
